import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown, EyeOff, Settings, ShieldCheck } from 'lucide-react'
import {
  PILL_CLASS,
  PILL_CLASS_TIGHT,
  TOP_BAR_CONTAINER_CLASS,
  PILL_SEPARATOR_CLASS,
  BREAKPOINTS,
} from './chartTokens.js'

// CE-04g-UI-chrome-1 — TradingView-style top bar for the new chassis.
//
// Layout (left → right):
//   1. Ticker pill (controlled input, commits on Enter or blur)
//   2. Pill separator
//   3. Timeframe pills — 12 entries, 8 enabled (1m / 2m / 5m / 15m / 30m / 65m / D / W),
//      4 disabled (10m / 1h / 4h / M) with a "Coming soon" tooltip.
//   4. Pill separator
//   5. Candles / Bars pill toggle
//   6. Pill separator
//   7. Indicators ▾ dropdown (optional)
//   8. Sticky chart settings button
//
// Shared by the canonical Position Workspace and Strategy Workspace. The
// chart's internal +Add and floating IndicatorAccordion overlay coexist with
// this dropdown; both add surfaces call the same `addIndicator(kind)` handle.
// See chart-component-contract.md §15.
// CE-04g-UI-chrome-2 — converted to forwardRef so the parent's keyboard
// handler can `focus()` the ticker input AND `setDraft(char)` without
// committing. Commit semantics remain Enter / blur on the input.

// Authoritative timeframe-pill table (from the spec's mapping). Disabled
// entries have `chassisValue === null` and never fire onTimeframeChange.
const TF_BAR = Object.freeze([
  { label: '1m',  chassisValue: '1min'  },
  { label: '2m',  chassisValue: '2min'  },
  { label: '5m',  chassisValue: '5min'  },
  { label: '10m', chassisValue: null    },
  { label: '15m', chassisValue: '15min' },
  { label: '30m', chassisValue: '30min' },
  { label: '65m', chassisValue: '65min' },
  { label: '1h',  chassisValue: null    },
  { label: '4h',  chassisValue: null    },
  { label: 'D',   chassisValue: 'daily' },
  { label: 'W',   chassisValue: 'weekly'},
  { label: 'M',   chassisValue: null    },
])

const CHART_TYPES = Object.freeze([
  { key: 'candlestick', label: 'Candles' },
  { key: 'bar',         label: 'Bars' },
])

function pillClass(active) {
  return `${PILL_CLASS.base} ${active ? PILL_CLASS.active : PILL_CLASS.inactive}`
}

// CE-04g-UI-chrome-1-fix-2 item C — tighter horizontal padding for the
// 12-pill TF row only. Other groups keep the standard pillClass.
function tfPillClass(active) {
  return `${PILL_CLASS_TIGHT.base} ${active ? PILL_CLASS_TIGHT.active : PILL_CLASS_TIGHT.inactive}`
}

const ChartTopBar = forwardRef(function ChartTopBar({
  symbol,
  onSymbolChange,
  timeframe,
  onTimeframeChange,
  marketSession = 'regular',
  onMarketSessionChange,
  extendedHoursEnabled = false,
  chartType,
  onChartTypeChange,
  chartTypeDisabled = false,
  onAddIndicator,
  onAddIndicatorTemplate,
  onToggleAllIndicatorsVisible,
  availableIndicators,
  indicatorTemplates = [],
  showIndicators,
  positionOverlayEnabled,
  positionOverlayTitle,
  onPositionOverlayToggle,
  onSettingsOpen,
  onSymbolSearchOpen,
  disabledTimeframes = [],
  additionalTools,
  layoutControl,
}, ref) {
  // Controlled-input pattern: local draft for the ticker pill, parent-owned
  // committed value (commit on Enter or blur; revert on Escape).
  const [draftSymbol, setDraftSymbol] = useState(symbol)
  useEffect(() => { setDraftSymbol(symbol) }, [symbol])

  const [menuOpen, setMenuOpen] = useState(false)
  const indicatorsRootRef = useRef(null)
  // CE-04g-UI-chrome-2 — ref on the ticker input so the parent's keyboard
  // handler can call `focus()` and `setDraft(char)` via the forwardRef
  // handle below.
  const inputRef = useRef(null)
  const scrollRef = useRef(null)

  useImperativeHandle(ref, () => ({
    focus() {
      inputRef.current?.focus()
    },
    setDraft(text) {
      setDraftSymbol(String(text).toUpperCase())
    },
  }), [])

  // Below md (768px) the dropdown menu opens full-width instead of using
  // its anchored min-width. Decided on open so a viewport rotation between
  // opens picks up the new width — re-evaluated on each click.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < BREAKPOINTS.md
  const showMarketSessionControl = typeof onMarketSessionChange === 'function'
  const showPositionOverlayToggle = typeof onPositionOverlayToggle === 'function'
  const showIndicatorCatalog = typeof onAddIndicator === 'function'
    && Array.isArray(availableIndicators)
    && availableIndicators.length > 0
  const showIndicatorTemplates = typeof onAddIndicatorTemplate === 'function'
    && Array.isArray(indicatorTemplates)
    && indicatorTemplates.length > 0
  const showIndicatorBulkToggle = typeof onToggleAllIndicatorsVisible === 'function'
  const showIndicatorsMenu = showIndicators !== false && (showIndicatorCatalog || showIndicatorTemplates || showIndicatorBulkToggle)

  // The Position Workspace has no inline dropdown, so its toolbar can scroll
  // at every width. Preserve dropdown overflow for the other chart surfaces.
  useEffect(() => {
    if (showIndicatorsMenu) return undefined
    const bar = scrollRef.current
    if (!bar) return undefined
    function onWheel(event) {
      if (event.ctrlKey || event.shiftKey || event.deltaX || !event.deltaY) return
      const maxScroll = bar.scrollWidth - bar.clientWidth
      if (maxScroll <= 0) return
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? bar.clientWidth : 1)
      const next = Math.max(0, Math.min(maxScroll, bar.scrollLeft + delta))
      if (next === bar.scrollLeft) return
      event.preventDefault()
      bar.scrollLeft = next
    }
    bar.addEventListener('wheel', onWheel, { passive: false })
    return () => bar.removeEventListener('wheel', onWheel)
  }, [showIndicatorsMenu])

  useEffect(() => {
    if (!menuOpen) return
    function onMouseDown(e) {
      if (indicatorsRootRef.current && !indicatorsRootRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  function commitSymbol() {
    const next = (draftSymbol || '').trim().toUpperCase()
    if (!next) {
      setDraftSymbol(symbol)
      return
    }
    if (next === symbol) return
    onSymbolChange(next)
  }

  function onTickerKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitSymbol()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setDraftSymbol(symbol)
      e.currentTarget.blur()
    }
  }

  function onTickerPointerDown(e) {
    if (!isMobile || typeof onSymbolSearchOpen !== 'function') return
    e.preventDefault()
    inputRef.current?.blur()
    onSymbolSearchOpen(draftSymbol || symbol)
  }

  return (
    <div ref={scrollRef} data-testid="chart-top-bar" className={`${TOP_BAR_CONTAINER_CLASS} ${showIndicatorsMenu ? 'sm:overflow-visible' : 'overflow-y-hidden'}`}>
      <div data-tb-group="ticker" className="sticky left-0 z-20 flex items-center bg-canvas pr-1">
        <input
          ref={inputRef}
          data-testid="chart-top-bar-ticker"
          type="text"
          aria-label="Symbol"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          value={draftSymbol}
          onChange={(e) => setDraftSymbol(e.target.value.toUpperCase())}
          onKeyDown={onTickerKeyDown}
          onPointerDown={onTickerPointerDown}
          onFocus={(e) => e.target.select()}
          onBlur={commitSymbol}
          className={`${pillClass(true)} text-base sm:text-xs text-center uppercase`}
          style={{ width: 76, fontSize: 16 }}
        />
      </div>

      <div data-tb-separator className={PILL_SEPARATOR_CLASS} />

      <div data-tb-group="timeframe" data-testid="chart-top-bar-tf-group" className="flex items-center gap-0">
        {TF_BAR.map(({ label, chassisValue }) => {
          const disabled = chassisValue === null || disabledTimeframes.includes(chassisValue)
          const active = !disabled && chassisValue === timeframe
          return (
            <button
              key={label}
              type="button"
              data-tb-tf={label}
              aria-disabled={String(disabled)}
              title={disabled ? 'Coming soon' : label}
              onClick={() => {
                if (disabled) return
                onTimeframeChange(chassisValue)
              }}
              className={`${tfPillClass(active)} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {label}
            </button>
          )
        })}
      </div>

      <div data-tb-separator className={PILL_SEPARATOR_CLASS} />

      {showMarketSessionControl && (
        <>
          <div data-tb-group="session" data-testid="chart-top-bar-session-group" className="flex items-center gap-1">
            <button
              type="button"
              data-testid="chart-top-bar-session-rth" data-command-id="position.chart.session.regular"
              aria-pressed={String(marketSession === 'regular')}
              data-command-label="Regular trading hours only"
              onClick={() => onMarketSessionChange('regular')}
              className={pillClass(marketSession === 'regular')}
            >
              RTH
            </button>
            <button
              type="button"
              data-testid="chart-top-bar-session-ext" data-command-id="position.chart.session.extended"
              aria-pressed={String(marketSession === 'all')}
              aria-disabled={String(!extendedHoursEnabled)}
              data-command-label={extendedHoursEnabled ? 'Include pre-market and after-hours trading' : 'Extended hours unavailable for this timeframe'}
              onClick={() => {
                if (!extendedHoursEnabled) return
                onMarketSessionChange('all')
              }}
              className={`${pillClass(marketSession === 'all')} ${extendedHoursEnabled ? '' : 'opacity-50 cursor-not-allowed'}`}
            >
              EXT
            </button>
          </div>
          <div data-tb-separator className={PILL_SEPARATOR_CLASS} />
        </>
      )}

      <div data-tb-group="chart-type" data-testid="chart-top-bar-chart-type-group" className="flex items-center gap-1">
        {CHART_TYPES.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            data-testid={`chart-top-bar-chart-type-${key}`}
            data-command-id={key === 'candlestick' ? 'position.chart.candles' : 'position.chart.bars'}
            disabled={chartTypeDisabled}
            aria-pressed={chartType === key}
            onClick={() => onChartTypeChange(key)}
            className={pillClass(chartType === key)}
          >
            {label}
          </button>
        ))}
      </div>

      {showPositionOverlayToggle && (
        <>
          <div data-tb-separator className={PILL_SEPARATOR_CLASS} />

          <div data-tb-group="position-overlay" data-testid="chart-top-bar-position-overlay-group" className="flex items-center">
            <button
              type="button"
              data-testid="chart-top-bar-position-overlay-toggle" data-command-id="position.chart.position.toggle"
              aria-pressed={String(positionOverlayEnabled === true)}
              data-command-label={positionOverlayTitle || 'Position overlay'}
              onClick={onPositionOverlayToggle}
              className={`${pillClass(positionOverlayEnabled === true)} flex items-center gap-1`}
            >
              <ShieldCheck size={12} aria-hidden />
              <span>Position</span>
            </button>
          </div>
        </>
      )}

      {showIndicatorsMenu && (
        <>
          <div data-tb-separator className={PILL_SEPARATOR_CLASS} />

          <div data-tb-group="indicators" ref={indicatorsRootRef} className="relative flex items-center">
            <button
              type="button"
              data-testid="chart-top-bar-indicators-button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className={`${pillClass(menuOpen)} flex items-center gap-1`}
            >
              <span>Indicators</span>
              <ChevronDown size={12} aria-hidden />
            </button>
            {menuOpen && (
              <div
                role="menu"
                data-testid="chart-top-bar-indicators-menu"
                data-tb-menu-width={isMobile ? 'full' : 'anchored'}
                className="absolute top-full mt-1 z-30 bg-canvas border border-border rounded-lg shadow-lg py-1 overflow-y-auto"
                style={
                  isMobile
                    ? { left: 0, right: 0, maxHeight: 320 }
                    : { left: 0, minWidth: 200, maxHeight: 320 }
                }
              >
                {showIndicatorBulkToggle && (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="chart-top-bar-toggle-all-indicators"
                      onClick={() => {
                        onToggleAllIndicatorsVisible()
                        setMenuOpen(false)
                      }}
                      className="w-full text-left px-3 py-1.5 text-xs text-inkMuted hover:text-ink hover:bg-canvas2 flex items-center gap-2"
                    >
                      <EyeOff size={12} aria-hidden />
                      <span>Hide / show all</span>
                    </button>
                    {(showIndicatorCatalog || showIndicatorTemplates) && (
                      <div className="mx-3 my-1 border-t border-border" />
                    )}
                  </>
                )}
                {showIndicatorCatalog && availableIndicators.map(({ kind, label }) => (
                  <button
                    key={kind}
                    type="button"
                    role="menuitem"
                    data-tb-indicator-row={kind}
                    onClick={() => {
                      onAddIndicator(kind)
                      setMenuOpen(false)
                    }}
                    className="w-full text-left px-3 py-1.5 text-xs text-inkMuted hover:text-ink hover:bg-canvas2"
                  >
                    {label}
                  </button>
                ))}
                {showIndicatorTemplates && (
                  <>
                    <div className="mx-3 my-1 border-t border-border" />
                    <div className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-inkDim">
                      Saved Templates
                    </div>
                    {indicatorTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        role="menuitem"
                        data-tb-indicator-template-row={template.id}
                        onClick={() => {
                          onAddIndicatorTemplate(template)
                          setMenuOpen(false)
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs text-inkMuted hover:text-ink hover:bg-canvas2"
                      >
                        <span className="block truncate">{template.name}</span>
                        <span className="block text-[10px] uppercase tracking-wider text-inkDim">{template.kind}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {additionalTools && <div data-tb-group="additional-tools" className="flex shrink-0 items-center">{additionalTools}</div>}

      {layoutControl && <div className="sticky right-8 z-20 ml-auto flex items-center bg-canvas">{layoutControl}</div>}

      {typeof onSettingsOpen === 'function' && (
        <button
          type="button"
          data-tb-group="settings"
          data-testid="chart-top-bar-settings-button"
          data-command-id="position.chart.settings.open"
          aria-label="Chart settings"
          title="Chart settings"
          onClick={onSettingsOpen}
          className={`${pillClass(false)} sticky right-0 z-20 ${layoutControl ? '' : 'ml-auto'} flex items-center justify-center !px-2 bg-canvas`}
        >
          <Settings size={14} strokeWidth={1.8} aria-hidden />
        </button>
      )}
    </div>
  )
})

export default ChartTopBar

ChartTopBar.propTypes = {
  symbol: PropTypes.string.isRequired,
  onSymbolChange: PropTypes.func.isRequired,
  timeframe: PropTypes.string.isRequired,
  onTimeframeChange: PropTypes.func.isRequired,
  marketSession: PropTypes.oneOf(['regular', 'all']),
  onMarketSessionChange: PropTypes.func,
  extendedHoursEnabled: PropTypes.bool,
  chartType: PropTypes.oneOf(['candlestick', 'bar']).isRequired,
  onChartTypeChange: PropTypes.func.isRequired,
  chartTypeDisabled: PropTypes.bool,
  onAddIndicator: PropTypes.func,
  onAddIndicatorTemplate: PropTypes.func,
  onToggleAllIndicatorsVisible: PropTypes.func,
  availableIndicators: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })),
  indicatorTemplates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    kind: PropTypes.string.isRequired,
  })),
  showIndicators: PropTypes.bool,
  positionOverlayEnabled: PropTypes.bool,
  positionOverlayTitle: PropTypes.string,
  onPositionOverlayToggle: PropTypes.func,
  onSettingsOpen: PropTypes.func,
  onSymbolSearchOpen: PropTypes.func,
  disabledTimeframes: PropTypes.arrayOf(PropTypes.string),
  additionalTools: PropTypes.node,
  layoutControl: PropTypes.node,
}
